/**
 * PayoutConnectBanner — Iter 161.
 *
 * Header banner on /provider-os/payouts that surfaces two things
 * clinicians kept asking for:
 *   1. Stripe Connect onboarding status — is the bank on file?
 *      Is Instant Payouts capability live? Are we in simulated mode?
 *   2. If entitlement=`instant_payouts` is granted AND there's a
 *      released balance to draw, an "Instant payout" CTA that
 *      triggers /api/clinician/banking/payout with type=instant.
 *
 * Falls back gracefully: if the banking endpoint 4xxes (e.g. the user
 * is an agency operator, not an individual clinician), we render
 * nothing and let the ledger below speak for itself.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, ShieldCheck, Landmark, ArrowRight, ExternalLink, Loader2, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlements, FEATURES } from '../../hooks/useEntitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '$0.00';
  return `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

export default function PayoutConnectBanner({ onPayoutSuccess }) {
  const { token } = useAuth();
  const { hasFeature } = useEntitlements();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingOut, setPayingOut] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/clinician/banking`,
        { headers: { Authorization: `Bearer ${token}` } });
      setState(data);
    } catch {
      // Not a clinician (agency/planner) — silently skip.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || !state) return null;

  const {
    banking = {}, balance = {}, connect = {}, simulated = true, instant_fee_pct: fee_pct,
  } = state;
  const available = Number(balance?.available || 0);
  const bankLinked = !!banking?.bank_linked;
  const connectReady = !!connect?.ready;
  const canInstant = hasFeature(FEATURES.INSTANT_PAYOUTS)
                     && banking?.instant_payouts_enabled
                     && available > 0;

  const requestInstantPayout = async () => {
    if (!canInstant) return;
    if (!window.confirm(
      `Request instant payout of ${usd(available)}? A ${(fee_pct || 0) * 100}% fast-pay fee applies.`,
    )) return;
    setPayingOut(true);
    try {
      const { data } = await axios.post(`${API}/clinician/banking/payout`,
        { amount: available, payout_type: 'instant' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(
        data?.simulated
          ? `Payout of ${usd(data?.net || available)} queued (simulated rail).`
          : `Payout of ${usd(data?.net || available)} sent.`,
      );
      await load();
      onPayoutSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Payout failed';
      toast.error(typeof msg === 'string' ? msg : 'Payout failed');
    } finally {
      setPayingOut(false);
    }
  };

  // -------- Rendering --------
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5"
      data-testid="payout-connect-banner"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {connectReady ? (
            <div className="rounded-full bg-emerald-100 p-2 flex-shrink-0" aria-hidden>
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
          ) : bankLinked ? (
            <div className="rounded-full bg-amber-100 p-2 flex-shrink-0" aria-hidden>
              <Landmark className="w-5 h-5 text-amber-600" />
            </div>
          ) : (
            <div className="rounded-full bg-slate-100 p-2 flex-shrink-0" aria-hidden>
              <AlertCircle className="w-5 h-5 text-slate-500" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-slate-900" data-testid="payout-status-label">
                {connectReady
                  ? 'Stripe Connect ready · payouts live'
                  : bankLinked
                  ? 'Bank on file · settlement simulated'
                  : 'No bank on file yet'}
              </div>
              {simulated && (
                <span
                  className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
                  data-testid="payout-mock-pill"
                >
                  MOCK
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {connectReady
                ? `Instant payouts route through Stripe (${(fee_pct || 0) * 100}% fast-pay fee).`
                : bankLinked
                ? `${banking.bank_name || 'Bank'} ••${banking.account_last4 || '—'} — payouts settle in the simulated ledger until Stripe Connect onboarding completes.`
                : 'Add a bank account to receive payouts. Instant payouts require Stripe Connect Express onboarding.'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Available to draw:{' '}
              <span className="font-semibold text-slate-900" data-testid="payout-available-balance">
                {usd(available)}
              </span>
              {balance?.pending ? (
                <> · Pending: <span className="text-slate-600">{usd(balance.pending)}</span></>
              ) : null}
            </p>
          </div>
        </div>

        <div className="flex flex-col md:items-end gap-2 md:gap-1.5 flex-shrink-0">
          {canInstant ? (
            <button
              type="button"
              onClick={requestInstantPayout}
              disabled={payingOut}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="request-instant-payout-btn"
            >
              {payingOut
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Zap className="w-4 h-4" />}
              Request instant payout · {usd(available)}
            </button>
          ) : bankLinked && !hasFeature(FEATURES.INSTANT_PAYOUTS) ? (
            <a
              href="/pricing?upsell=instant_payouts"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
              data-testid="instant-payout-upsell"
            >
              Unlock instant payouts <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <a
              href="/clinician/banking"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
              data-testid="setup-payouts-cta"
            >
              {bankLinked ? 'Finish Stripe onboarding' : 'Add a bank account'}
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <a
            href="/clinician/banking"
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="banking-hub-link"
          >
            Banking hub <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
